import React, { useState, useEffect, useRef, useMemo } from "react";
import { canEdit } from "../utils/permissions";
import { getAllPhases } from "../services/azureProjectPhasesService";

const ProjectOverviewTable = ({
  projects,
  employees,
  user,
  onCreateProject,
  onEdit,
  onView,
  onControlling,
  onStundenAuszug,
  onProjectPage,
  onProjektstand,
  loading,
  readOnly = false,
  highlightedProjectId = null,
  typeFilter,
  setTypeFilter,
  showCompleted,
  setShowCompleted,
  isFreelancer = false,
}) => {
  // Check if user can access Stundenauszug (only MF-Admin and MF-PL)
  const canAccessStundenAuszug = user ? canEdit(user, 'stundenAuszug') : false;
  const canAccessProjektstand = user ? canEdit(user, 'projektstand') : false;
  const tableRef = useRef(null);
  const [sortField, setSortField] = useState("code");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [allPhases, setAllPhases] = useState([]);

  // Load all phases once
  useEffect(() => {
    getAllPhases().then(setAllPhases).catch(() => {});
  }, []);

  // Group active phases by project code
  const activePhasesByProject = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const map = {};
    for (const phase of allPhases) {
      if (phase.startDate && phase.startDate <= today && (!phase.endDate || phase.endDate >= today)) {
        if (!map[phase.projectNumber]) map[phase.projectNumber] = [];
        map[phase.projectNumber].push(phase.name);
      }
    }
    return map;
  }, [allPhases]);

  // Scroll to highlighted project when it changes
  useEffect(() => {
    if (highlightedProjectId) {
      const row = document.getElementById(`project-row-${highlightedProjectId}`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [highlightedProjectId]);

  const getEmployeeName = (employeeUuid) => {
    const employee = employees.find((emp) => emp.uuid === employeeUuid);
    return employee
      ? `${employee.firstName} ${employee.lastName}`
      : "Unbekannt";
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredAndSortedProjects = projects
    .filter((project) => {
      // Filter by status (ongoing vs completed)
      if (!showCompleted) {
        const today = new Date().toISOString().split("T")[0];
        const isOngoing = !project.projektende || project.projektende >= today;
        if (!isOngoing) return false;
      }

      // Filter by search term
      return (
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getEmployeeName(project.pic).toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Spezielle Behandlung für PIC (Employee Name)
      if (sortField === "pic") {
        aValue = getEmployeeName(a.pic);
        bValue = getEmployeeName(b.pic);
      }

      // Datum-Sortierung
      if (sortField === "projektstart" || sortField === "projektende") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("de-CH");
  };

  if (loading) {
    return <div className="loading">Projekte werden geladen...</div>;
  }

  return (
    <div className="project-overview-table">
      {/* Filter Controls */}
      <div className="team-controls">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Projekte durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          {/* Hide type filter for freelancers */}
          {!isFreelancer && (
            <select
              className="filter-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="project">Projects</option>
              <option value="offerte">Offerten</option>
              <option value="other">Other</option>
            </select>
          )}
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Abgeschlossene anzeigen
          </label>
        </div>
        <div className="table-info">
          {filteredAndSortedProjects.length} von {projects.length} Projekten
        </div>
      </div>

      {/* Table */}
      <div className="team-table-container">
        <table className="team-table team-table-compact">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("code")}
                className={`sortable ${
                  sortField === "code" ? "active" : ""
                }`}
              >
                code
                {sortField === "code" && (
                  <span className="sort-indicator">
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th
                onClick={() => handleSort("title")}
                className={`sortable ${sortField === "title" ? "active" : ""}`}
              >
                Titel
                {sortField === "title" && (
                  <span className="sort-indicator">
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th
                onClick={() => handleSort("projektstart")}
                className={`sortable ${
                  sortField === "projektstart" ? "active" : ""
                }`}
              >
                Projektstart
                {sortField === "projektstart" && (
                  <span className="sort-indicator">
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th
                onClick={() => handleSort("projektende")}
                className={`sortable ${
                  sortField === "projektende" ? "active" : ""
                }`}
              >
                Projektende
                {sortField === "projektende" && (
                  <span className="sort-indicator">
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedProjects.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-results">
                  {searchTerm
                    ? "Keine Projekte gefunden"
                    : "Noch keine Projekte vorhanden"}
                </td>
              </tr>
            ) : (
              filteredAndSortedProjects.map((project) => (
                <tr
                  key={project.id}
                  id={`project-row-${project.id}`}
                  onClick={() => onView(project)}
                  className={`team-row clickable-row ${highlightedProjectId === project.id ? "highlighted" : ""}`}
                >
                  <td className="cell-nummer">
                    <div className="project-number-with-color">
                      <div
                        className="project-color-dot"
                        style={{ backgroundColor: project.color || "#6b7280" }}
                        title={`Projektfarbe: ${project.color || "#6b7280"}`}
                      />
                      <span className="project-number-text">
                        {project.code}
                      </span>
                    </div>
                  </td>
                  <td className="cell-name">
                    {project.title}
                    {activePhasesByProject[project.code]?.map((name, i) => (
                      <span key={i} className="phase-badge">{name}</span>
                    ))}
                  </td>
                  <td>{formatDate(project.projektstart)}</td>
                  <td>{formatDate(project.projektende)}</td>
                  <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                    {onProjectPage && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onProjectPage(project);
                        }}
                      >
                        Projektseite
                      </button>
                    )}
                    {!readOnly && onControlling && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onControlling(project);
                        }}
                      >
                        Controlling
                      </button>
                    )}
                    {!readOnly && onStundenAuszug && canAccessStundenAuszug && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStundenAuszug(project);
                        }}
                      >
                        Stundenauszug
                      </button>
                    )}
                    {!readOnly && onProjektstand && canAccessProjektstand && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onProjektstand(project);
                        }}
                      >
                        Projektstand
                      </button>
                    )}
                    {!readOnly && onEdit && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(project);
                        }}
                      >
                        Bearbeiten
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectOverviewTable;
